import { useState } from 'react';
import { useMeContext } from '../app/MeContext';
import { useSheet, useToast } from '../app/shell-context';
import { ApiError } from '../api/client';
import { addDaysISO, formatDay, localToUtc, todayISO } from '../lib/time';
import { pluralWithCount } from '../lib/plural';
import { Avatar, Notice, Pill } from '../ui/parts';
import { useArchiveSpecialist, usePauseSpecialist, useBatchStats } from './api';
import type { AdminSpecialist, PauseResult, Recipient } from './types';

/**
 * Пауза записи — самый опасный экран админки. Новые записи закрываются сразу,
 * а судьбу уже созданных админ выбирает явно. Молча потерять родителей нельзя:
 * тех, кому бот написать не может, показываем отдельным списком с телефонами —
 * это семьи, которые иначе придут к закрытой двери.
 */
export function PauseSheet({ specialist }: { specialist: AdminSpecialist }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const pause = usePauseSpecialist();
  const tz = me.center.tz;

  const [forever, setForever] = useState(false);
  const [until, setUntil] = useState(() => addDaysISO(todayISO(tz), 14));
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'keep' | 'cancel' | 'notify'>('keep');
  const [template, setTemplate] = useState(() => defaultTemplate(specialist.display_name, null, me.center.adminContact));

  const untilTs = forever ? null : localToUtc(until, 24 * 60 - 1, tz);
  const hasUpcoming = specialist.upcoming > 0;

  // Текст пересобираем, пока админ его не трогал руками: дата паузы в нём есть.
  const [touched, setTouched] = useState(false);
  const shownTemplate = touched
    ? template
    : defaultTemplate(specialist.display_name, forever ? null : formatDay(untilTs ?? Date.now(), tz), me.center.adminContact);

  function submit() {
    pause.mutate(
      {
        id: specialist.id,
        until: untilTs,
        reason: reason.trim(),
        appointments: mode,
        template: mode === 'keep' ? undefined : shownTemplate,
      },
      {
        onSuccess: (res) => sheet.open(<PauseResultSheet specialist={specialist} result={res} mode={mode} />),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось приостановить'),
      },
    );
  }

  return (
    <>
      <h3>Приостановить запись</h3>
      <p>
        {specialist.display_name}. Новые записи закроются сразу: клиенты перестанут видеть свободные
        окна. Закрепления, услуги и ставки останутся как есть.
      </p>

      <div className="field">
        <label htmlFor="pTill">До какого числа</label>
        <input id="pTill" type="date" value={until} min={todayISO(tz)} disabled={forever} onChange={(e) => setUntil(e.target.value)} />
      </div>
      <label className="check">
        <input type="checkbox" checked={forever} onChange={(e) => setForever(e.target.checked)} />
        <span>Бессрочно — до ручного возобновления</span>
      </label>

      <div className="field">
        <label htmlFor="pReason">Причина</label>
        <input id="pReason" type="text" placeholder="Болезнь, отпуск, декрет" value={reason} onChange={(e) => setReason(e.target.value)} />
        <span className="hint">Видно только в журнале организации, родителям не показывается.</span>
      </div>

      {hasUpcoming ? (
        <>
          <Notice>
            Уже есть {pluralWithCount(specialist.upcoming, 'будущая запись', 'будущие записи', 'будущих записей')}.
            Что с ними делать?
          </Notice>
          <div className="field">
            <label htmlFor="pKeep">Существующие записи</label>
            <select id="pKeep" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="keep">Оставить — специалист их проведёт</option>
              <option value="cancel">Отменить с возвратом оплаты</option>
              <option value="notify">Оставить и написать родителям</option>
            </select>
          </div>
        </>
      ) : (
        <Notice kind="calm">Будущих записей нет — пауза никого не затронет.</Notice>
      )}

      {mode !== 'keep' ? (
        <div className="field">
          <label htmlFor="pTpl">Текст сообщения родителям</label>
          <textarea
            id="pTpl"
            style={{ minHeight: 120 }}
            value={shownTemplate}
            onChange={(e) => {
              setTouched(true);
              setTemplate(e.target.value);
            }}
          />
          <span className="hint">
            Подстановки {'{родитель}'}, {'{ребёнок}'}, {'{дата и время}'} бот заполнит для каждого получателя;
            {' {админ}'} развернётся в {me.center.adminContact || 'контакт администратора'} из карточки администратора.
          </span>
        </div>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button className="btn solid-danger" disabled={pause.isPending} onClick={submit}>
          {pause.isPending ? 'Приостанавливаем…' : 'Приостановить'}
        </button>
      </div>
    </>
  );
}

/**
 * Архивация — та же развилка, что у паузы, но без даты: специалист не вернётся,
 * и обещать перенос «к нему же» нельзя. Раньше архивация молчала, и семьи с
 * будущими записями узнавали об уходе специалиста у закрытой двери.
 */
export function ArchiveSheet({ specialist }: { specialist: AdminSpecialist }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const archive = useArchiveSpecialist();
  const hasUpcoming = specialist.upcoming > 0;
  const [mode, setMode] = useState<'keep' | 'cancel' | 'notify'>(hasUpcoming ? 'notify' : 'keep');
  const [template, setTemplate] = useState(() => defaultArchiveTemplate(specialist.display_name, me.center.adminContact));

  function submit() {
    archive.mutate(
      { id: specialist.id, appointments: hasUpcoming ? mode : 'keep', template: mode === 'keep' ? undefined : template },
      {
        onSuccess: (res) => {
          if (res.batch) sheet.open(<PauseResultSheet specialist={specialist} result={res} mode={mode} archived />);
          else {
            toast('Специалист архивирован');
            sheet.close();
          }
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось архивировать'),
      },
    );
  }

  return (
    <>
      <h3>Архивировать специалиста</h3>
      <p>
        {specialist.display_name} пропадёт из записи и из дня организации — записаться к нему
        будет нельзя. Карточка останется в этом списке с пометкой «архив», история записей
        и расчёты сохранятся: на них держатся отчёты за прошлые месяцы.
      </p>

      {hasUpcoming ? (
        <>
          <Notice>
            У специалиста {pluralWithCount(specialist.upcoming, 'будущая запись', 'будущие записи', 'будущих записей')}.
            Что с ними делать?
          </Notice>
          <div className="field">
            <label htmlFor="aKeep">Существующие записи</label>
            <select id="aKeep" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="notify">Оставить и написать родителям</option>
              <option value="cancel">Отменить с возвратом оплаты</option>
              <option value="keep">Оставить молча — разберусь сам</option>
            </select>
          </div>
        </>
      ) : (
        <Notice kind="calm">Будущих записей нет — архивировать безопасно.</Notice>
      )}

      {hasUpcoming && mode !== 'keep' ? (
        <div className="field">
          <label htmlFor="aTpl">Текст сообщения родителям</label>
          <textarea id="aTpl" style={{ minHeight: 120 }} value={template} onChange={(e) => setTemplate(e.target.value)} />
          <span className="hint">
            Подстановки {'{родитель}'}, {'{ребёнок}'}, {'{дата и время}'} бот заполнит для каждого получателя.
          </span>
        </div>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button className="btn solid-danger" disabled={archive.isPending} onClick={submit}>
          {archive.isPending ? 'Архивируем…' : 'Архивировать'}
        </button>
      </div>
    </>
  );
}

/**
 * Результат паузы или архивации: кому ушло, а кому бот написать не может.
 * Второй список — главный: этим родителям надо позвонить руками.
 */
function PauseResultSheet({ specialist, result, mode, archived = false }: {
  specialist: AdminSpecialist;
  result: PauseResult;
  mode: 'keep' | 'cancel' | 'notify';
  archived?: boolean;
}) {
  const sheet = useSheet();
  const stats = useBatchStats(result.batch?.batchId ?? null);

  const reachable = result.recipients.filter((r) => r.chatId !== null);
  const unreachable = result.recipients.filter((r) => r.chatId === null);

  return (
    <>
      <h3>{archived ? 'Специалист архивирован' : 'Запись приостановлена'}</h3>
      <p>
        {specialist.display_name}
        {archived ? '' : result.specialist.paused_until ? ` — до ${new Date(result.specialist.paused_until).toLocaleDateString('ru-RU')}` : ' — бессрочно'}.
        {mode === 'cancel' ? ' Записи отменены, оплаты помечены к возврату.' : ''}
        {mode === 'notify' ? ' Записи остались, родителям уходит сообщение.' : ''}
        {mode === 'keep' ? ' Уже созданные записи не тронуты.' : ''}
      </p>

      {result.batch ? (
        <div className="kv">
          <div>
            <span className="k">В очереди</span>
            <span className="v">{stats.data?.queued ?? result.batch.queued}</span>
          </div>
          <div>
            <span className="k">Отправлено</span>
            <span className="v">{stats.data?.sent ?? 0}</span>
          </div>
          {stats.data?.failed ? (
            <div>
              <span className="k">Не доставлено</span>
              <span className="v">{stats.data.failed}</span>
            </div>
          ) : null}
          <div>
            <span className="k">Бот написать не может</span>
            <span className="v">{result.batch.noContact}</span>
          </div>
        </div>
      ) : null}

      {reachable.length ? (
        <>
          <p className="eyebrow">Уйдёт в Telegram</p>
          <div className="rows">
            {reachable.map((r) => <RecipientRow key={r.clientId} recipient={r} reachable />)}
          </div>
        </>
      ) : null}

      {unreachable.length ? (
        <>
          <p className="eyebrow">Этим позвоните сами</p>
          <Notice>
            {pluralWithCount(unreachable.length, 'семья не получит', 'семьи не получат', 'семей не получат')} сообщение:
            бот не может написать первым тому, кто ему не писал, а заблокировавшим — вообще. Позвоните по телефонам ниже.
          </Notice>
          <div className="rows">
            {unreachable.map((r) => <RecipientRow key={r.clientId} recipient={r} reachable={false} />)}
          </div>
        </>
      ) : null}

      {!result.recipients.length ? <p className="lead">{archived ? 'Будущих записей не было — писать некому.' : 'Записей на период паузы не было — писать некому.'}</p> : null}

      <button className="btn solid" onClick={() => sheet.close()}>Готово</button>
    </>
  );
}

function RecipientRow({ recipient, reachable }: { recipient: Recipient; reachable: boolean }) {
  return (
    <div className="rw">
      <Avatar name={recipient.parentName || recipient.childName} small />
      <span className="rw-body">
        <b>{recipient.parentName} · {recipient.childName}</b>
        <span>{recipient.when.join(', ')}</span>
      </span>
      {reachable ? <Pill kind="paid">в Telegram</Pill> : <Pill kind="await">{recipient.phone || 'нет телефона'}</Pill>}
    </div>
  );
}

/**
 * Тот же текст, что собирает сервер по умолчанию: админ должен его видеть и
 * править до отправки. Фраза про личку администратора обязательна — иначе
 * родители пойдут с вопросами в пустоту.
 */
function defaultArchiveTemplate(name: string, adminContact: string): string {
  return (
    `Здравствуйте, {родитель}! ${name} больше не работает в организации. ` +
    'Ваша запись: {ребёнок}, {дата и время}. ' +
    'Мы свяжемся с вами, чтобы предложить другого специалиста или вернуть оплату. ' +
    `Если остались вопросы — напишите администратору в личку: ${adminContact || '{админ}'}. ` +
    'Извините за неудобство.'
  );
}

function defaultTemplate(name: string, untilHuman: string | null, adminContact: string): string {
  return (
    `Здравствуйте, {родитель}! ${name} временно не ведёт записи` +
    `${untilHuman ? ` — до ${untilHuman}` : ''}. ` +
    'Ваша запись: {ребёнок}, {дата и время} — попадает на это время. ' +
    'Мы свяжемся с вами, чтобы перенести запись или вернуть оплату. ' +
    `Если запись отменят и у вас останутся вопросы — напишите администратору в личку: ${adminContact || '{админ}'}. ` +
    'Извините за неудобство.'
  );
}
